import { routes } from "../../lib/nav";
import { SITE_URL } from "../../lib/site";
import type { CaseStudy } from "./types";

const dir = "/cases/quantum-clinic";

const img = (file: string, alt: string, span: 2 | 3 | 6 = 6, width = 1600, height = 900) => ({
  src: `${dir}/${file}.webp`,
  src900: `${dir}/${file}-900.webp`,
  alt,
  width,
  height,
  wide: span === 6,
  span,
});

const mobile = (file: string, alt: string, height = 1238) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 760,
  height,
  span: 2 as const,
});

export const quantumClinic: CaseStudy = {
  slug: "quantum-clinic",
  title: "Quantum Clinic",
  cardTitle: "Разработка и продвижение сайта сети клиник Quantum Clinic",
  subtitle: "Сеть клиник эстетической медицины",
  industry: { label: "Красота и косметология", slug: "beauty" },
  eyebrow: "Корпоративный сайт · Эстетическая медицина",
  h1: "Разработка и продвижение сайта Quantum Clinic",
  lead:
    "Собрали сайт сети клиник эстетической медицины Quantum Clinic: каталог услуг по направлениям и проблемам, страницы специалистов, оборудования и клиник сети, онлайн-запись — и ведём продвижение проекта.",
  heroButtons: { site: "Смотреть сайт", discuss: "Обсудить сайт и продвижение клиники" },

  seo: {
    primaryKeyword: "разработка и продвижение сайта клиники косметологии",
    secondaryKeywords: [
      "разработка сайта косметологической клиники",
      "продвижение клиники эстетической медицины",
      "сайт для сети клиник",
      "SEO клиники косметологии",
      "сайт с онлайн-записью для клиники",
    ],
    semanticEntities: [
      "сеть клиник",
      "эстетическая медицина",
      "каталог услуг",
      "страницы специалистов",
      "оборудование клиники",
      "онлайн-запись",
      "спецпредложения",
      "программа лояльности",
      "геоканалы",
    ],
    excludedKeywords: [
      "косметология Москва",
      "SMAS-лифтинг цена",
      "BBL Москва",
      "записаться к косметологу",
    ],
    title: "Сайт и продвижение Quantum Clinic — кейс HAY.agency",
    description:
      "Кейс HAY.agency: сайт сети клиник эстетической медицины Quantum Clinic, каталог услуг, специалисты, оборудование, онлайн-запись и продвижение.",
    h1: "Разработка и продвижение сайта Quantum Clinic",
    slug: "quantum-clinic",
    canonicalUrl: `${SITE_URL}/cases/quantum-clinic`,
    ogTitle: "Сайт и продвижение сети клиник Quantum Clinic",
    ogDescription:
      "Каталог услуг по направлениям и проблемам, специалисты, оборудование, клиники сети и онлайн-запись.",
    ogImage: `${SITE_URL}${dir}/quantum-clinic-og.webp`,
    robotsIndex: true,
    datePublished: "2024-03-01",
    dateModified: "2026-09-01",
  },

  links: {
    relatedService: {
      label: "Разработка корпоративных сайтов",
      to: routes.corporate,
      note: "Структура услуг, специалисты и заявки",
    },
    relatedIndustry: {
      label: "Продвижение медицинских центров и клиник",
      to: routes.expert,
      note: "SEO, реклама и геоканалы для медицинского бизнеса",
    },
    relatedCases: [
      {
        label: "Интернет-магазин профессиональной косметики Quantum Shop",
        to: "/cases/quantum-shop",
        note: "E-commerce-проект той же группы компаний",
      },
      {
        label: "Комплексное продвижение сети «Городская База Цветов»",
        to: "/cases/gbcvet",
        note: "Сайт сети, SEO и геоперформанс по нескольким локациям",
      },
    ],
    relatedArticles: [],
  },

  cta: {
    title: "Нужен сайт клиники, который приводит записи?",
    text: "Спроектируем каталог услуг, страницы специалистов и запись, возьмём продвижение на себя.",
    button: "Обсудить сайт и продвижение клиники",
  },

  client: {
    name: "Quantum Clinic",
    url: "https://quantum-clinic.ru/",
    city: "Москва",
    industry: "Эстетическая медицина",
  },
  year: "С 2024 года",
  services: [
    "Дизайн сайта клиники",
    "Разработка каталога услуг",
    "Страницы специалистов и клиник сети",
    "Онлайн-запись",
    "SEO-продвижение",
    "Сопровождение проекта",
  ],
  platform: "1С-Битрикс",
  tags: [
    { label: "Корпоративный сайт", to: routes.corporate },
    { label: "Разработка", to: routes.sites },
    { label: "SEO", to: routes.seo },
    { label: "Яндекс Директ", to: routes.context },
    { label: "Геоперформанс", to: routes.geo },
  ],
  facts: [
    { label: "Клиент", value: "Quantum Clinic" },
    { label: "Отрасль", value: "Эстетическая медицина" },
    { label: "Тип проекта", value: "Сайт сети клиник с онлайн-записью" },
    { label: "Платформа", value: "1С-Битрикс" },
  ],

  results: {
    title: "Что реализовали",
    items: [
      "Сайт сети с раздельными сценариями «Для женщин» и «Для мужчин»",
      "Каталог услуг по направлениям и проблемам с переходом к процедуре",
      "Страницы специалистов и оборудования клиники",
      "Отдельные страницы клиник: Новокузнецкая, Ходынка, Москва-Сити, Хамовники",
      "Разделы спецпредложений, программы лояльности, «До/после» и блога",
      "Онлайн-запись и переход в магазин Quantum Shop",
    ],
  },

  story: [
    {
      title: "Задача",
      paragraphs: [
        "Эстетическая медицина — сложный выбор: пациент приходит с проблемой, а не с названием процедуры, сравнивает клиники, специалистов и оборудование и только потом записывается. Сети клиник нужен был сайт, который выдерживает большой перечень услуг и при этом остаётся понятным на первом визите.",
        "Вторая часть задачи — единая структура для нескольких локаций: каждая клиника сети должна иметь собственную страницу, но работать в общей навигации и вести к одной и той же записи.",
      ],
    },
  ],
  features: [],

  design: {
    title: "Дизайн: спокойный премиальный тон вместо медицинской стерильности",
    paragraphs: [
      "Интерфейс построен на тёплой ivory-палитре, крупной типографике и большом воздухе — сайт читается как премиальный бренд, а не как медицинский справочник.",
      "Первый экран сразу разводит два сценария — «Для женщин» и «Для мужчин», — чтобы посетитель попадал в свой раздел услуг за один клик.",
    ],
    bullets: [
      "Тёплая ivory/stone-палитра без стерильного белого",
      "Крупные заголовки и понятные точки входа в услуги",
      "Запись доступна с любой страницы услуги или специалиста",
    ],
  },

  identity: {
    fonts: [
      { name: "Гротеск Light", role: "Крупные заголовки", sample: "Для женщин" },
      { name: "Гротеск Regular", role: "Текст и навигация", sample: "Услуги клиники Quantum" },
    ],
    colors: [
      { hex: "#F7F3EE", name: "Основной фон" },
      { hex: "#C4A882", name: "Акцент бренда" },
      { hex: "#1B1C20", name: "Текст" },
    ],
  },

  cover: img(
    "quantum-clinic-case-cover",
    "Главная страница сайта сети клиник Quantum Clinic с разделами «Для женщин» и «Для мужчин»",
    6,
  ),

  gallery: [
    img(
      "quantum-clinic-services-catalog",
      "Каталог услуг Quantum Clinic по направлениям и проблемам",
      6,
    ),
  ],

  responsive: {
    text: [
      "Большая часть записей приходит с телефона, поэтому мобильные сценарии проектировали первыми: выбор направления, услуга, специалист и запись.",
      "В мобильной версии услуга, клиника и кнопка записи находятся в одном экране — пациенту не нужно возвращаться назад, чтобы принять решение.",
    ],
    images: [
      mobile("quantum-clinic-mobile-home", "Мобильная главная сайта Quantum Clinic"),
      mobile("quantum-clinic-mobile-services", "Мобильный каталог услуг клиники"),
    ],
  },

  columnBlocks: [
    {
      id: "catalog",
      title: "Сложный каталог услуг",
      paragraphs: [
        "Каталог построен по логике выбора пациента: направление → проблема → процедура → специалист и оборудование → запись. Так посетитель, который ищет решение проблемы, а не название методики, доходит до записи без консультации по телефону.",
      ],
      columns: [
        {
          title: "Направление",
          items: ["Косметология лица", "Аппаратные методики", "Инъекционные процедуры"],
        },
        {
          title: "Проблема и процедура",
          items: ["Подбор по проблеме", "Описание процедуры", "Показания и подготовка"],
        },
        {
          title: "Специалист и запись",
          items: ["Профиль специалиста", "Оборудование клиники", "Онлайн-запись"],
        },
      ],
      caption: "Каждая ветка каталога заканчивается записью, а не тупиковой страницей.",
    },
    {
      id: "network",
      title: "Сеть клиник в единой структуре",
      paragraphs: [
        "Клиники на Новокузнецкой, Ходынке, в Москва-Сити и Хамовниках получили одинаковую структуру страниц: адрес, специалисты, услуги локации и запись. Это упрощает и навигацию пациенту, и работу с геоканалами.",
      ],
      bullets: [
        "Единый шаблон страницы клиники для всех локаций",
        "Специалисты и услуги привязаны к конкретной клинике",
        "Контакты и запись повторяются на каждой локальной странице",
      ],
    },
    {
      id: "content",
      title: "Контент, который помогает выбрать",
      paragraphs: [
        "Помимо услуг на сайте работают разделы, которые закрывают сомнения перед первой процедурой: оборудование клиники, галерея «До/после», спецпредложения, программа лояльности и блог. Отдельная точка входа ведёт в интернет-магазин Quantum Shop.",
      ],
      columns: [
        { title: "Доверие", items: ["Оборудование", "До/после", "Сертификаты"] },
        { title: "Мотивация", items: ["Спецпредложения", "Программа лояльности"] },
        { title: "Экспертность", items: ["Блог клиники", "Профили специалистов"] },
      ],
    },
    {
      id: "promotion",
      title: "Продвижение проекта",
      paragraphs: [
        "После запуска сайт остаётся на нашем сопровождении: работаем с поисковой видимостью услуг и локаций, ведём рекламные кампании и геоканалы. Показатели раскроем после согласования с клиентом.",
      ],
      bullets: [
        "SEO по услугам и страницам клиник сети",
        "Реклама в Яндекс Директе",
        "Геоканалы и карточки клиник на картах",
        "Техническая поддержка и развитие сайта",
      ],
    },
  ],

  servicesDone: {
    title: "Комплекс работ HAY.agency",
    items: [
      { label: "Разработка корпоративного сайта", to: routes.corporate },
      { label: "SEO-продвижение", to: routes.seo },
      { label: "Яндекс Директ", to: routes.context },
      { label: "Геоперформанс и карты", to: routes.geo },
      { label: "Поддержка и развитие", to: routes.corporate },
    ],
  },
};
